import { callGeotabAPI } from '../api/geotab-client';
import { splitDateRange, mapLimit, MAX_CONCURRENT } from '../api/fetch-range';
import type { Rule, Device, User, ExceptionEvent, LogRecord } from '../api/types';
import type { HeatPoint, HeatmapAnalysisParams, RuleKey } from './heatmap-types';
import { RULE_KEY_ORDER } from './heatmap-types';

const EVENT_LIMIT  = 50000;
const LOG_LIMIT    = 5000;  // GPS records fetched per device per weekly window
const GPS_WIN_DAYS = 7;     // spread each device's GPS budget across the range, week by week
const DEVICE_BATCH = 8;     // devices processed in parallel during the GPS phase
const MAX_DEVICES  = 500;   // covers typical fleets in full; only a guard against runaway GPS
                            // fetches on very large databases. Memory no longer scales with this
                            // (logs are correlated then discarded per device), so it can be high.

// ── Fuzzy match — mirrors safety-data.ts ──────────────────────────────────

const FUZZY: Record<RuleKey, string[]> = {
  hardAccel:      ['hard accel', 'harsh accel'],
  harshBraking:   ['harsh brak', 'hard brak'],
  harshCornering: ['harsh corner', 'hard corner'],
  seatBelt:       ['seat belt', 'seatbelt'],
  speeding:       ['speed'],
  idling:         ['idl'],
};

// ── Public init loader (cached after first call) ───────────────────────────

let _cachedPageData: HeatmapPageData | null = null;

export interface MatchedRule {
  id: string;
  name: string;
}

export interface HeatmapPageData {
  ruleIdsMap: Map<RuleKey, string[]>;
  matchedRuleNames: Map<RuleKey, MatchedRule[]>;
  deviceList: Array<{ id: string; name: string }>;
  userMap: Map<string, string>;
}

export async function loadHeatmapPageData(forceRefresh = false): Promise<HeatmapPageData> {
  if (_cachedPageData && !forceRefresh) return _cachedPageData;

  const [rawRules, rawDevices, rawUsers] = await Promise.all([
    callGeotabAPI<Rule[]>  ('Get', { typeName: 'Rule',   resultsLimit: 10000 }),
    callGeotabAPI<Device[]>('Get', { typeName: 'Device', resultsLimit: 5000  }),
    callGeotabAPI<User[]>  ('Get', { typeName: 'User',   resultsLimit: 5000  }),
  ]);

  const now = new Date();

  const ruleIdsMap      = new Map<RuleKey, string[]>();
  const matchedRuleNames = new Map<RuleKey, MatchedRule[]>();

  for (const key of RULE_KEY_ORDER) {
    const keywords = FUZZY[key];
    const matches = rawRules.filter(r =>
      r.id && r.name && keywords.some(kw => r.name.toLowerCase().includes(kw))
    );
    if (matches.length > 0) {
      ruleIdsMap.set(key, matches.map(r => r.id));
      matchedRuleNames.set(key, matches.map(r => ({ id: r.id, name: r.name })));
    }
  }

  const deviceList = rawDevices
    .filter(d => d.id && d.name && d.serialNumber && (!d.activeTo || new Date(d.activeTo) > now))
    .map(d => ({ id: d.id, name: d.name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const userMap = new Map<string, string>();
  rawUsers
    .filter(u => u.id && u.id !== 'UnknownDriverId' && (!u.activeTo || new Date(u.activeTo) > now))
    .forEach(u => {
      const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.name || u.id;
      userMap.set(u.id, name);
    });

  _cachedPageData = { ruleIdsMap, matchedRuleNames, deviceList, userMap };
  return _cachedPageData;
}

export function resetHeatmapPageData(): void {
  _cachedPageData = null;
}

// ── Binary search: nearest LogRecord by dateTime ──────────────────────────

function binarySearchNearest(records: LogRecord[], isoTarget: string): LogRecord | null {
  if (records.length === 0) return null;
  const target = new Date(isoTarget).getTime();
  let lo = 0, hi = records.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (new Date(records[mid].dateTime).getTime() < target) lo = mid + 1;
    else hi = mid;
  }
  const candidates = [records[lo]];
  if (lo > 0) candidates.push(records[lo - 1]);
  candidates.sort((a, b) =>
    Math.abs(new Date(a.dateTime).getTime() - target) -
    Math.abs(new Date(b.dateTime).getTime() - target)
  );
  const best = candidates[0];
  const diffMs = Math.abs(new Date(best.dateTime).getTime() - target);
  return diffMs <= 15 * 60 * 1000 ? best : null; // within 15 min of event time
}

// ── Fetch heat points: ExceptionEvents + LogRecord correlation ─────────────

export async function fetchHeatPoints(
  params: HeatmapAnalysisParams,
  onProgress: (msg: string) => void,
): Promise<HeatPoint[]> {
  // Flatten ruleIdsMap to individual {key, ruleId} tasks
  const fetchTasks: Array<{ key: RuleKey; ruleId: string }> = [];
  for (const [key, ruleIds] of params.ruleIdsMap) {
    for (const ruleId of ruleIds) {
      fetchTasks.push({ key, ruleId });
    }
  }
  if (fetchTasks.length === 0) return [];

  onProgress('Fetching violation events…');

  // 1. Fetch ExceptionEvents per rule, concurrency-capped. The cap (EVENT_LIMIT) is intentional:
  //    this is a density map, not an exact count, and 50k points per rule already saturates the
  //    visualization — pulling every event over a long range is needless memory pressure.
  const perRule = await mapLimit(fetchTasks, MAX_CONCURRENT, async ({ key, ruleId }) => {
    const events = await callGeotabAPI<ExceptionEvent[]>('Get', {
      typeName: 'ExceptionEvent',
      search: { ruleSearch: { id: ruleId }, fromDate: params.fromISO, toDate: params.toISO },
      resultsLimit: EVENT_LIMIT,
    });
    return { key, events };
  });

  // 2. Collect unique device IDs that actually had events
  const deviceEventMap = new Map<string, Array<{ ruleKey: RuleKey; event: ExceptionEvent }>>();
  for (const { key, events } of perRule) {
    for (const ev of events) {
      const devId = ev.device.id;
      if (!params.deviceIds.has(devId)) continue;
      if (!deviceEventMap.has(devId)) deviceEventMap.set(devId, []);
      deviceEventMap.get(devId)!.push({ ruleKey: key, event: ev });
    }
  }

  let deviceIdsWithEvents = [...deviceEventMap.keys()];
  if (deviceIdsWithEvents.length === 0) return [];

  // 3. Cap devices to prevent browser memory crash on very large fleets
  let cappedWarning = false;
  if (deviceIdsWithEvents.length > MAX_DEVICES) {
    // Keep the devices with the most events (most relevant for heatmap)
    deviceIdsWithEvents = deviceIdsWithEvents
      .sort((a, b) => (deviceEventMap.get(b)?.length ?? 0) - (deviceEventMap.get(a)?.length ?? 0))
      .slice(0, MAX_DEVICES);
    cappedWarning = true;
  }

  // 4. Fetch GPS logs per device and correlate immediately, then discard the logs.
  //    We can't pull full 30-day breadcrumb trails for the whole fleet (millions of records →
  //    freeze), so each device's GPS budget is capped per call. Geotab returns the EARLIEST
  //    records first, so a single whole-range call only covers a device's first couple of days
  //    and every later event finds no nearby fix and is dropped — that's the heavy undercount on
  //    long ranges. Instead we walk the range one week at a time and fetch (≤ LOG_LIMIT) logs per
  //    week, so coverage is spread across the whole window. Logs are correlated then discarded
  //    per week, so memory stays tiny regardless of range length.
  const PAD_MS     = 15 * 60 * 1000;
  const gpsWindows = splitDateRange(params.fromISO, params.toISO, GPS_WIN_DAYS);
  const heatPoints: HeatPoint[] = [];
  let done = 0;
  const totalDev = deviceIdsWithEvents.length;
  onProgress(`Fetching GPS positions (0 of ${totalDev})…`);

  // One retry on failure so a transient/rate-limited call doesn't silently drop a whole window.
  const fetchLogs = async (devId: string, from: string, to: string): Promise<LogRecord[] | null> => {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        return await callGeotabAPI<LogRecord[]>('Get', {
          typeName: 'LogRecord',
          search: { deviceSearch: { id: devId }, fromDate: from, toDate: to },
          resultsLimit: LOG_LIMIT,
        });
      } catch {
        if (attempt === 0) await new Promise(r => setTimeout(r, 400));
      }
    }
    return null;
  };

  await mapLimit(deviceIdsWithEvents, DEVICE_BATCH, async devId => {
    const tagged = deviceEventMap.get(devId) ?? [];

    for (const [wFrom, wTo] of gpsWindows) {
      const wFromMs = new Date(wFrom).getTime();
      const wToMs   = new Date(wTo).getTime();
      const winEvents = tagged.filter(t => {
        const ts = new Date(t.event.activeFrom).getTime();
        return ts >= wFromMs && ts < wToMs;
      });
      if (winEvents.length === 0) continue;   // skip weeks this device had no violations

      const logs = await fetchLogs(
        devId,
        new Date(wFromMs - PAD_MS).toISOString(),
        new Date(wToMs + PAD_MS).toISOString(),
      );
      if (!logs || logs.length === 0) continue;
      logs.sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());

      for (const { ruleKey, event } of winEvents) {
        const record = binarySearchNearest(logs, event.activeFrom);
        if (!record) continue;
        heatPoints.push({
          latitude:  record.latitude,
          longitude: record.longitude,
          ruleKey,
          deviceId:  devId,
          driverId:  event.driver?.id ?? null,
          timestamp: event.activeFrom,
          intensity: 1.0,
        });
      }
      // this week's logs fall out of scope here and are freed before the next week.
    }

    done++;
    if (done % DEVICE_BATCH === 0 || done === totalDev)
      onProgress(`Fetching GPS positions (${done} of ${totalDev})…`);
  });

  if (cappedWarning) {
    onProgress(`Note: capped at ${MAX_DEVICES} vehicles (largest event counts). Results may be partial.`);
    await new Promise(r => setTimeout(r, 1500));
  }

  return heatPoints;
}

